import { ulid } from 'ulid';
import type { ScenarioViewModel } from '@/graphql/generated';
import type { ScenarioConditionFormState } from './scenario-input';

type ScenarioCloneInput = Pick<ScenarioViewModel,
  | 'name'
  | 'scenarioConditions'
>;

export interface ClonedScenarioData {
  scenarioId: string;
  name: string;
  conditions: ScenarioConditionFormState[];
}

/**
 * Convert a date string to local date format (YYYY-MM-DD)
 */
const toLocalDate = (dateString?: string | null): string | undefined => {
  if (!dateString) return undefined;
  return new Date(dateString).toISOString().split('T')[0];
};

/**
 * Clone a Scenario entity with all its conditions
 *
 * - scenarioId: NEW (regenerated)
 * - facilityId: Handled server-side (not included in CreateScenarioInput)
 * - scenarioConditions: Converted to form state format
 * - All other fields: COPIED as-is
 */
export function cloneScenario(source: ScenarioCloneInput): ClonedScenarioData {
  // Convert scenario conditions to form state format with NEW condition IDs
  const convertedConditions: ScenarioConditionFormState[] =
    source.scenarioConditions?.map((sc: any) => {
      const c = sc.condition;
      return {
        // Spread base fields from condition first
        ...c,
        // Then override with new ID and transformed fields (these take precedence)
        conditionId: ulid(), // NEW ID for the cloned condition - must come AFTER spread
        operator: sc.operator?.toLowerCase() === 'exclude' ? 'exclude' : 'include',
        startDate: toLocalDate(c?.startDate),
        endDate: toLocalDate(c?.endDate),
        // Map nested arrays to string IDs
        selectedVehicleTypes: c?.conditionVehicleTypes?.map((vt: any) => vt.vehicleTypeId.toString()) || [],
        selectedCustomerCategories: c?.conditionCustomerCategories?.map((cc: any) => cc.customerCategoryId.toString()) || [],
        selectedPaymentMethods: c?.conditionPaymentMethods?.map((pm: any) => pm.paymentMethodId.toString()) || [],
        selectedDaysOfWeek: c?.conditionDayOfWeeks?.map((dow: any) => dow.dayOfWeekId.toString()) || [],
      };
    }) || [];

  return {
    scenarioId: ulid(),
    name: `${source.name} (Copy)`,
    conditions: convertedConditions,
  };
}
